import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { Icon } from "@iconify/react/dist/iconify.js";
import { CallEnd, Loop, Mic, MicOff, PhonePaused } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import "./InAppCall.css";
import Loader from "../Components/Loader";
import Timer from "../Helper/Timer";
import LogoMoxcal from "../Images/xonly.png";

import {
  addInAppCallSessionRetryValue,
  checkInAppCallSession,
  endInAppCallSession,
  getExtension,
  releaseExtension,
  setOriginate,
  setUsedExtension,
  startInAppCallSession,
} from "../Store/Actions/CallAction";

import {
  callDirect,
  clearSessionCall,
  holdCall,
  muteCall,
  registerSIP,
  unregisterSIP,
} from "../Store/SIP/call";

const InAppCall = ({ videoCall }) => {
  const [extension, setExtension] = useState("");
  const [isOpen, setIsOpen] = useState(true);
  const [isSessionActive, setIsSessionActive] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const [mediaPermissionError, setMediaPermissionError] = useState(
    "Please allow microphone and camera access to continue."
  );

  const [status, setStatus] = useState("Dialing...");
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const dispatch = useDispatch();

  const { inAppCallStatus, inAppCallType, isMuted, onHold } = useSelector(
    (state) => state.call
  );

  async function checkMediaPermissions() {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Media devices are not supported by this browser.");
    }

    const constraints = {
      audio: true,
      video: videoCall,
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // This is only a permission check; the call will acquire its own stream.
      stream.getTracks().forEach((track) => track.stop());

      setMediaPermissionError("");
      return true;
    } catch (error) {
      console.error("Media permission error:", error);

      setMediaPermissionError(
        error.name === "NotAllowedError"
          ? "Please allow microphone and camera access to continue."
          : "Unable to access the required media devices."
      );

      return false;
    }
  }

  function isWebRTCSupported() {
    return Boolean(
      window.RTCPeerConnection &&
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia
    );
  }

  useEffect(() => {
    let cancelled = false;

    async function initializePage() {
      const hasPermission = await checkMediaPermissions();

      if (cancelled || !hasPermission) {
        return;
      }

      if (!isWebRTCSupported()) {
        setStatus(
          "Your browser does not support WebRTC. Please use another browser."
        );
        setIsSessionActive(false);
        return;
      }

      window.addEventListener("beforeunload", alertUser);
      window.addEventListener("unload", handleTabClosing);
      const open = moment().hour(9);
      const close = moment().hour(24);

      if (moment().isBetween(open, close)) {
        setIsOpen(true);
        checkSession();
      } else {
        setIsOpen(false);
        setStatus(
          "We are currently closed. Please try again tomorrow at 09:00"
        );
      }
    }

    initializePage();

    return () => {
      cancelled = true;
      window.removeEventListener("beforeunload", alertUser);
      window.removeEventListener("unload", handleTabClosing);
    };
  }, []);

  useEffect(() => {
    if (extension) {
      if (inAppCallStatus === "registered") {
        setStatus("Connecting...");

        if (!videoCall) {
          console.log({
            callerid: extension.match(/\d+/)[0],
            channel: extension,
            context: "internal",
            exten: "6000",
          });

          setOriginate({
            callerid: extension.match(/\d+/)[0],
            channel: extension,
            context: "internal",
            exten: "6000",
          })
            .then((response) => {
              if (response.status) {
                console.log("setOriginate", response);

                // setUsedExtension(localStorage.getItem("sessionID"))
                setUsedExtension(
                  new URLSearchParams(window.location.search).get("sessionid")
                )
                  .then((response) => {
                    if (response.status) {
                      console.log("setUsedExtension", response);
                    } else {
                      console.log("setUsedExtension", response);

                      toast.error("Error using extension", {
                        autoClose: 3000,
                      });
                    }
                  })
                  .catch((err) => {
                    console.log("err", err);

                    toast.error("Error using extension", {
                      autoClose: 3000,
                    });
                  });
              } else {
                console.log("setOriginate", response);
                release();

                toast.error("Error connecting call", {
                  autoClose: 3000,
                });
              }
            })
            .catch((err) => {
              console.log("err", err);
              release();

              toast.error("Error connecting call", {
                autoClose: 3000,
              });
            });
        } else {
          dispatch(callDirect());
        }
      } else if (inAppCallStatus === "started") {
        setStatus("Connected");
      } else {
        release();
      }
    }
  }, [inAppCallStatus]);

  useEffect(() => {
    let localVideoStream;

    if (inAppCallType === "video") {
      navigator.mediaDevices
        .getUserMedia({ audio: true, video: true })
        .then((stream) => {
          localVideoStream = stream;
          console.log("localVideoStream", localVideoStream);

          if (localVideoRef.current) {
            localVideoRef.current.srcObject = stream;
          }
        })
        .catch((error) => {
          console.error("Error accessing local media:", error);
        });
    }

    return () => {
      localVideoStream?.getTracks().forEach((track) => track.stop());
    };
  }, [inAppCallType]);

  function alertUser(event) {
    event.preventDefault();
    event.returnValue = "";
  }

  function checkSession() {
    const sessionID = new URLSearchParams(window.location.search).get(
      "sessionid"
    );

    setIsVerifying(true);

    checkInAppCallSession(sessionID)
      .then((response) => {
        if (response.status) {
          if (response.data) {
            !extension && fetchExtension();
            setIsSessionActive(true);
            setIsVerifying(false);
          } else {
            setIsSessionActive(false);
            setIsVerifying(false);
          }
        } else {
          console.log("error", response.error);
          setIsVerifying(false);

          toast.error("Error verifying session ID", {
            autoClose: 3000,
          });
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsVerifying(false);

        toast.error("Error verifying session ID", {
          autoClose: 3000,
        });
      });
  }

  function fetchExtension() {
    // let sessionID = localStorage.getItem("sessionID");
    const sessionID = new URLSearchParams(window.location.search).get(
      "sessionid"
    );

    /* if (!sessionID) {
      sessionID = crypto.randomUUID(); // Generate unique ID
      localStorage.setItem("sessionID", sessionID);
    } */

    getExtension(sessionID)
      .then((responseGet) => {
        if (responseGet.status) {
          if (responseGet.data.extension) {
            const SIPAccount = responseGet.data.sipAccount;

            startInAppCallSession({
              ext: responseGet.data.extension,
              sessionid: sessionID,
            })
              .then((responseStart) => {
                if (responseStart.status) {
                  setExtension(responseGet.data.extension);
                  setStatus("Dialing...");

                  dispatch(
                    registerSIP(
                      SIPAccount.webrtcUser,
                      SIPAccount.webrtcPass,
                      SIPAccount.displayName,
                      SIPAccount.websocketSIP,
                      SIPAccount.websocketURL,
                      null,
                      null,

                      videoCall
                        ? (stream) => {
                            // This runs when remote stream is received
                            if (remoteVideoRef.current) {
                              remoteVideoRef.current.srcObject = stream;
                            }
                          }
                        : null
                    )
                  );
                } else {
                  console.log("error", responseStart.error);

                  toast.error("Error starting session", {
                    autoClose: 3000,
                  });
                }
              })
              .catch((err) => {
                console.log("err", err);

                toast.error("Error starting session", {
                  autoClose: 3000,
                });
              });
          } else {
            setStatus("Waiting for extension...");
            setTimeout(fetchExtension, 3000);
          }
        } else {
          console.log("error", responseGet.error);
          release();

          toast.error("Error getting extension", {
            autoClose: 3000,
          });
        }
      })
      .catch((err) => {
        console.log("err", err);
        release();

        toast.error("Error getting extension", {
          autoClose: 3000,
        });
      });
  }

  function handleTabClosing() {
    release();
    // localStorage.removeItem("sessionID");
  }

  function release() {
    const sessionID = new URLSearchParams(window.location.search).get(
      "sessionid"
    );

    dispatch(clearSessionCall());
    dispatch(unregisterSIP());

    addInAppCallSessionRetryValue({ sessionid: sessionID })
      .then((response) => {
        if (response.status) {
          const data = response.data
          console.log("retry logged", data);

          toast.info(
            `Session retries left: ${data.maxretries - data.retries} retries`,

            {
              autoClose: 3000,
            }
          );

          if (data.maxretries === data.retries) {
            endInAppCallSession({ sessionid: sessionID })
              .then((response) => {
                if (response.status) {
                  console.log("session ended");
                } else {
                  console.log("error", response.error);

                  toast.error("Error ending session: " + response.error, {
                    autoClose: 3000,
                  });
                }
              })
              .catch((err) => {
                console.log("err", err);

                toast.error("Error ending session: " + err, {
                  autoClose: 3000,
                });
              });
          }
        } else {
          console.log("error", response.error);

          toast.error("Error adding session retry value: " + response.error, {
            autoClose: 3000,
          });
        }
      })
      .catch((err) => {
        console.log("err", err);

        toast.error("Error adding session retry value: " + err, {
          autoClose: 3000,
        });
      });

    // releaseExtension(localStorage.getItem("sessionID"))
    releaseExtension(sessionID)
      .then((response) => {
        if (response.status) {
          console.log("released");
        } else {
          console.log("error", response.error);
        }
      })
      .catch((err) => {
        console.log("err", err);
      });

    setStatus("Disconnected");
    setExtension("");
  }

  return (
    <div
      className={`in-app-call landingContainerOrigin ${
        videoCall ? "video-call" : ""
      }`}
    >
      <div className="landingChat">
        {mediaPermissionError ? (
          <div className="expired-session">
            <h4>Device permission required</h4>
            <p>{mediaPermissionError}</p>
          </div>
        ) : isVerifying ? (
          <Loader desc={"Verifying session..."} />
        ) : isSessionActive ? (
          <>
            {videoCall && (
              <>
                <video
                  autoPlay
                  className="agent-video"
                  controls={false}
                  loop
                  playsInline
                  ref={remoteVideoRef}
                />

                <video
                  autoPlay
                  className="customer-video"
                  controls={false}
                  muted
                  playsInline
                  ref={localVideoRef}
                />
              </>
            )}

            <section className="center">
              {!videoCall && <img src={LogoMoxcal} alt="" className="logo" />}
              <p className="call-status">{status}</p>

              {!videoCall && (
                <>
                  <p className="contact-name">Customer Service</p>

                  <p className="timer">
                    <Timer isPaused={inAppCallStatus !== "started"} />
                  </p>
                </>
              )}
            </section>

            <section className="controls">
              {inAppCallStatus === "started" && (
                <Tooltip title={onHold ? "Resume" : "Hold"} arrow>
                  <IconButton
                    sx={{
                      width: "3rem",
                      height: "3rem",
                      bgcolor: onHold ? "#FFF" : "#727272",
                      color: onHold ? "#727272" : "#FFF",
                      boxShadow: "0px 2px 18px 1px rgba(0, 0, 0, 0.18)",

                      "&:hover": {
                        backgroundColor: onHold ? "#c4c4c4" : "#848484",
                      },
                    }}
                    onClick={() => dispatch(holdCall())}
                  >
                    <PhonePaused />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip
                title={
                  inAppCallStatus === "started" ? "End call" : "Reload page"
                }
                arrow
              >
                <IconButton
                  disabled={onHold}
                  sx={{
                    width: "4rem",
                    height: "4rem",
                    bgcolor: inAppCallStatus === "started" ? "#F24E1E" : "blue",
                    color: "#FFF",
                    boxShadow: "0px 2px 18px 1px rgba(0, 0, 0, 0.18)",

                    "&:hover": {
                      backgroundColor:
                        inAppCallStatus === "started" ? "#ff7d58" : "darkblue",
                    },
                  }}
                  onClick={() => {
                    if (inAppCallStatus === "started") {
                      release();
                    } else {
                      window.location.reload();
                    }
                  }}
                >
                  {inAppCallStatus === "started" || extension ? (
                    <CallEnd fontSize="large" />
                  ) : (
                    <Loop fontSize="large" />
                  )}
                </IconButton>
              </Tooltip>

              {inAppCallStatus === "started" && (
                <Tooltip title={isMuted ? "Unmute" : "Mute"} arrow>
                  <IconButton
                    sx={{
                      width: "3rem",
                      height: "3rem",
                      bgcolor: "#727272",
                      color: "#FFF",
                      boxShadow: "0px 2px 18px 1px rgba(0, 0, 0, 0.18)",
                      "&:hover": { backgroundColor: "#848484" },
                    }}
                    disabled={onHold}
                    onClick={() => dispatch(muteCall())}
                  >
                    {isMuted ? <Mic /> : <MicOff />}
                  </IconButton>
                </Tooltip>
              )}
            </section>

            {!videoCall && extension && (
              <p className="extension">Ext: {extension.match(/\d+/)[0]}</p>
            )}
          </>
        ) : (
          <div className="expired-session">
            {!isVerifying && !extension && (
              <>
                <Icon
                  height="120"
                  icon={isOpen ? "pajamas:time-out" : "cuida:moon-outline"}
                />

                {isOpen ? (
                  <>
                    <h4>This session has been expired!</h4>

                    <p>
                      Please contact our customer support to request a new
                      session
                    </p>
                  </>
                ) : (
                  <>
                    <h4>We are currently closed</h4>
                    <p>Please try again tomorrow at 09:00</p>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InAppCall;
